import React from 'react';

import {MODEL_RUN_SOURCE} from 'constants/AnalyticsConstants';
import Modal from 'ui-library/modals/Modal';

import {MODEL_CATEGORY, type ScientificModelNode} from '../ScientificModelMetadata';
import {AntibodyInputForm} from './antibody/AntibodyInputForm';
import {BoltzGenInputForm} from './boltzgen/BoltzGenInputForm';
import StructurePredictionForm from './structure-prediction/StructurePredictionForm';

export interface ScientificModelInputFormProps {
  model: ScientificModelNode;
  onRequestClose: () => void;
  onSubmit?: () => Promise<void>;
}

export function ScientificModelInputForm({
  model,
  onRequestClose,
  onSubmit,
}: ScientificModelInputFormProps): JSX.Element {
  if (model.inputTypes.includes('antibody') && !model.capabilities.supportsBulkPredictions) {
    return (
      <AntibodyInputForm
        model={model}
        onRequestClose={onRequestClose}
        onSubmit={onSubmit}
        source={MODEL_RUN_SOURCE.MODEL_HUB_UI}
      />
    );
  } else if (model.category === MODEL_CATEGORY.GENERATIVE) {
    return (
      <BoltzGenInputForm
        model={model}
        onRequestClose={onRequestClose}
        source={MODEL_RUN_SOURCE.MODEL_HUB_UI}
      />
    );
  } else if (model.category === MODEL_CATEGORY.STRUCTURE_PREDICTION) {
    return (
      <StructurePredictionForm
        availableModels={[model]}
        onSubmit={onRequestClose}
        startingModel={model}
        source={MODEL_RUN_SOURCE.MODEL_HUB_UI}
      />
    );
  } else {
    throw new Error(`Unsupported model type: ${model}`);
  }
}

export interface ScientificModelInputFormModalProps {
  model: ScientificModelNode;
  onRequestClose: () => void;
  onRequestSubmit: (success: boolean) => Promise<void>;
}

export function ScientificModelInputFormModal({
  model,
  onRequestClose,
  onRequestSubmit,
}: ScientificModelInputFormModalProps): JSX.Element {
  const isCompact = !model.capabilities.supportsBulkPredictions;

  return (
    <Modal
      title={`Configure ${model.displayName}`}
      onRequestClose={onRequestClose}
      closeableOnBackgroundClick={true}
      large={!isCompact}
      medium={isCompact}
      fullHeight={!isCompact}
      bodyPadded={false}
    >
      <ScientificModelInputForm
        model={model}
        onRequestClose={onRequestClose}
        onSubmit={() => onRequestSubmit(true)}
      />
    </Modal>
  );
}
